/**
 * Wishlist — localStorage backed, zero dependencies.
 *
 * Architecture notes:
 * - One observable store owns the data. Nothing else reads localStorage.
 * - UI is made of custom elements that subscribe on connect and unsubscribe on
 *   disconnect. Because custom elements auto-upgrade, DOM injected later by the
 *   Section Rendering API works with no MutationObserver — which is what caused
 *   the original infinite loop (writing textContent inside a childList observer
 *   re-triggered the observer forever).
 * - Product lookups are cached and de-duplicated, renders are token-guarded, and
 *   a render never re-runs for an unchanged item list.
 */
(() => {
  'use strict';

  const STORAGE_KEY = 'cb_wishlist';
  const MAX_ITEMS = 250;
  const UNDO_TIMEOUT = 8000;

  /** handle -> { status: 'ok', product } | { status: 'missing' } */
  const CACHE = new Map();
  /** handle -> in-flight promise, so concurrent renders share one request */
  const INFLIGHT = new Map();

  const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ------------------------------------------------------------------ store */

  function readStorage() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return [...new Set(parsed.filter((h) => typeof h === 'string' && h.length > 0))].slice(0, MAX_ITEMS);
    } catch (error) {
      return [];
    }
  }

  class WishlistStore extends EventTarget {
    #items = readStorage();

    get items() {
      return this.#items.slice();
    }

    get size() {
      return this.#items.length;
    }

    /** Cheap identity for the current list — used to short-circuit renders. */
    get key() {
      return this.#items.join('|');
    }

    has(handle) {
      return this.#items.includes(handle);
    }

    add(handle, position = 0) {
      if (!handle || this.has(handle)) return;
      const next = this.#items.slice();
      next.splice(Math.max(0, Math.min(position, next.length)), 0, handle);
      this.#commit(next.slice(0, MAX_ITEMS));
    }

    remove(handle) {
      if (!this.has(handle)) return;
      this.#commit(this.#items.filter((h) => h !== handle));
    }

    removeMany(handles) {
      const drop = new Set(handles);
      if (!this.#items.some((h) => drop.has(h))) return;
      this.#commit(this.#items.filter((h) => !drop.has(h)));
    }

    toggle(handle) {
      if (this.has(handle)) this.remove(handle);
      else this.add(handle);
      return this.has(handle);
    }

    indexOf(handle) {
      return this.#items.indexOf(handle);
    }

    /** Adopt state written by another tab. */
    sync() {
      const next = readStorage();
      if (next.join('|') === this.key) return;
      this.#items = next;
      this.#emit();
    }

    #commit(next) {
      this.#items = next;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (error) {
        /* Private mode / quota — keep working in memory for this page view. */
      }
      this.#emit();
    }

    #emit() {
      this.dispatchEvent(new CustomEvent('change', { detail: { items: this.items } }));
    }
  }

  const store = new WishlistStore();
  window.wishlistStore = store;

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) store.sync();
  });

  /* ----------------------------------------------------------- announcements */

  let liveRegion = null;

  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      Object.assign(liveRegion.style, {
        position: 'absolute',
        width: '1px',
        height: '1px',
        margin: '-1px',
        padding: '0',
        overflow: 'hidden',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
        border: '0',
      });
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  }

  /* --------------------------------------------------------------- utilities */

  function formatMoney(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  function sizedImage(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  async function fetchProduct(handle) {
    if (CACHE.has(handle)) return CACHE.get(handle);
    if (INFLIGHT.has(handle)) return INFLIGHT.get(handle);

    const request = (async () => {
      try {
        const response = await fetch(`${routeRoot()}products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (response.status === 404 || response.status === 410) {
          const result = { status: 'missing' };
          CACHE.set(handle, result);
          return result;
        }
        if (!response.ok) return { status: 'error' };
        const product = await response.json();
        const result = { status: 'ok', product };
        CACHE.set(handle, result);
        return result;
      } catch (error) {
        // Offline / aborted: never cache and never prune, or we would silently
        // delete the customer's saved products on a flaky connection.
        return { status: 'error' };
      } finally {
        INFLIGHT.delete(handle);
      }
    })();

    INFLIGHT.set(handle, request);
    return request;
  }

  /* ------------------------------------------------------------ <wishlist-button> */

  class WishlistButton extends HTMLElement {
    #button = null;
    #onStoreChange = () => this.#sync();

    connectedCallback() {
      this.#button = this.querySelector('button');
      if (!this.#button) return;
      this.#button.addEventListener('click', this.#onClick);
      store.addEventListener('change', this.#onStoreChange);
      this.#sync();
    }

    disconnectedCallback() {
      if (this.#button) this.#button.removeEventListener('click', this.#onClick);
      store.removeEventListener('change', this.#onStoreChange);
    }

    get handle() {
      return this.dataset.productHandle || '';
    }

    get productTitle() {
      return this.dataset.productTitle || '';
    }

    #onClick = (event) => {
      // Product card media usually sits inside a link — never navigate.
      event.preventDefault();
      event.stopPropagation();
      if (!this.handle) return;

      const active = store.toggle(this.handle);
      this.dataset.animate = 'true';
      setTimeout(() => delete this.dataset.animate, 400);
      announce(
        active
          ? `${this.productTitle || 'Product'} added to your wishlist`
          : `${this.productTitle || 'Product'} removed from your wishlist`
      );
    };

    #sync() {
      if (!this.#button) return;
      const active = store.has(this.handle);
      const name = this.productTitle;
      this.#button.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.#button.setAttribute(
        'aria-label',
        active
          ? name
            ? `Remove ${name} from wishlist`
            : 'Remove from wishlist'
          : name
            ? `Add ${name} to wishlist`
            : 'Add to wishlist'
      );
      this.toggleAttribute('active', active);
    }
  }

  /* ------------------------------------------------------------- <wishlist-count> */

  class WishlistCount extends HTMLElement {
    #onStoreChange = () => this.#sync();

    connectedCallback() {
      store.addEventListener('change', this.#onStoreChange);
      this.#sync();
    }

    disconnectedCallback() {
      store.removeEventListener('change', this.#onStoreChange);
    }

    #sync() {
      const count = store.size;
      this.textContent = count === 0 ? '' : count > 99 ? '99+' : String(count);
      this.hidden = count === 0;
      this.setAttribute('aria-hidden', 'true');

      // The bubble is decorative; the accessible name lives on the link.
      const link = this.closest('a');
      if (link) {
        link.setAttribute('aria-label', count === 0 ? 'Wishlist' : `Wishlist (${count} item${count === 1 ? '' : 's'})`);
      }
    }
  }

  /* -------------------------------------------------------------- <wishlist-page> */

  class WishlistPage extends HTMLElement {
    #token = 0;
    #renderedKey = null;
    #pendingFocusIndex = null;
    #undoTimer = null;
    #onStoreChange = () => this.render();

    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      store.addEventListener('change', this.#onStoreChange);
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      store.removeEventListener('change', this.#onStoreChange);
      clearTimeout(this.#undoTimer);
    }

    get grid() {
      return this.querySelector('[data-wishlist-grid]');
    }

    get emptyState() {
      return this.querySelector('[data-wishlist-empty]');
    }

    get undoBar() {
      return this.querySelector('[data-wishlist-undo]');
    }

    #onClick = (event) => {
      const undo = event.target.closest('[data-wishlist-undo-action]');
      if (undo && this.contains(undo)) {
        event.preventDefault();
        const { handle, position } = undo.dataset;
        store.add(handle, Number(position) || 0);
        this.#hideUndo();
        announce('Restored to your wishlist');
        return;
      }

      const remove = event.target.closest('[data-wishlist-remove]');
      if (!remove || !this.contains(remove)) return;
      event.preventDefault();

      const card = remove.closest('[data-handle]');
      const handle = card && card.dataset.handle;
      if (!handle) return;

      const cards = [...this.querySelectorAll('[data-handle]')];
      this.#pendingFocusIndex = Math.max(0, cards.indexOf(card));

      const position = store.indexOf(handle);
      const title = remove.dataset.productTitle || 'Product';
      store.remove(handle);
      announce(`${title} removed from your wishlist`);
      this.#showUndo(handle, position, title);
    };

    #showUndo(handle, position, title) {
      const bar = this.undoBar;
      if (!bar) return;
      bar.innerHTML = '';

      const text = document.createElement('span');
      text.className = 'wishlist-undo__text';
      text.textContent = `${title} removed.`;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wishlist-undo__action';
      button.textContent = 'Undo';
      button.dataset.wishlistUndoAction = '';
      button.dataset.handle = handle;
      button.dataset.position = String(position);

      bar.append(text, button);
      bar.hidden = false;

      clearTimeout(this.#undoTimer);
      this.#undoTimer = setTimeout(() => this.#hideUndo(), UNDO_TIMEOUT);
    }

    #hideUndo() {
      const bar = this.undoBar;
      if (!bar) return;
      clearTimeout(this.#undoTimer);
      bar.hidden = true;
      bar.innerHTML = '';
    }

    #setBusy(busy) {
      const grid = this.grid;
      if (grid) grid.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    #showSkeletons(count) {
      const grid = this.grid;
      if (!grid) return;
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < Math.min(count, 8); i += 1) {
        const item = document.createElement('li');
        item.className = 'wishlist-card wishlist-card--skeleton';
        item.setAttribute('aria-hidden', 'true');
        item.innerHTML =
          '<div class="wishlist-card__media wishlist-skeleton"></div>' +
          '<div class="wishlist-skeleton wishlist-skeleton--line"></div>' +
          '<div class="wishlist-skeleton wishlist-skeleton--line wishlist-skeleton--short"></div>';
        fragment.appendChild(item);
      }
      grid.replaceChildren(fragment);
      this.#setBusy(true);
    }

    async render() {
      const grid = this.grid;
      if (!grid) return;

      const key = store.key;
      // Guard: identical list means nothing to do. This is what stops the
      // change -> render -> change feedback loop.
      if (key === this.#renderedKey) return;
      this.#renderedKey = key;

      const token = (this.#token += 1);
      const handles = store.items;

      if (handles.length === 0) {
        grid.replaceChildren();
        this.#setBusy(false);
        this.#toggleEmpty(true);
        this.#applyPendingFocus(0);
        return;
      }

      this.#toggleEmpty(false);

      if (handles.some((handle) => !CACHE.has(handle))) {
        this.#showSkeletons(handles.length);
      }

      const results = await Promise.all(handles.map(fetchProduct));
      if (token !== this.#token) return; // superseded by a newer render

      const fragment = document.createDocumentFragment();
      const missing = [];

      handles.forEach((handle, index) => {
        const result = results[index];
        if (result.status === 'ok') fragment.appendChild(this.#buildCard(result.product));
        else if (result.status === 'missing') missing.push(handle);
        else fragment.appendChild(this.#buildErrorCard(handle));
      });

      grid.replaceChildren(fragment);
      this.#setBusy(false);
      this.#toggleEmpty(grid.children.length === 0 && missing.length === handles.length);
      this.#applyPendingFocus(grid.children.length);

      // Single prune after everything settled. The follow-up render is served
      // entirely from cache, so it terminates immediately.
      if (missing.length) store.removeMany(missing);
    }

    #toggleEmpty(isEmpty) {
      const empty = this.emptyState;
      if (empty) empty.hidden = !isEmpty;
    }

    #applyPendingFocus(cardCount) {
      if (this.#pendingFocusIndex === null) return;
      const index = this.#pendingFocusIndex;
      this.#pendingFocusIndex = null;

      requestAnimationFrame(() => {
        const buttons = [...this.querySelectorAll('[data-wishlist-remove]')];
        const target =
          buttons[Math.min(index, buttons.length - 1)] ||
          this.querySelector('[data-wishlist-undo-action]') ||
          (cardCount === 0 ? this.querySelector('[data-wishlist-empty] a') : null);
        if (target) target.focus({ preventScroll: true });
      });
    }

    #buildCard(product) {
      const card = document.createElement('li');
      card.className = 'wishlist-card';
      card.dataset.handle = product.handle;

      const link = document.createElement('a');
      link.className = 'wishlist-card__link';
      link.href = `${routeRoot()}products/${product.handle}`;

      const media = document.createElement('div');
      media.className = 'wishlist-card__media';
      const source = product.featured_image || (product.images && product.images[0]);
      if (source) {
        const img = document.createElement('img');
        // Property assignment, not innerHTML — no escaping bugs possible.
        img.src = sizedImage(source, 400);
        img.srcset = [200, 300, 400, 600, 800].map((w) => `${sizedImage(source, w)} ${w}w`).join(', ');
        img.sizes = '(min-width: 990px) 260px, (min-width: 750px) 33vw, 45vw';
        img.alt = product.title || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 400;
        img.height = 400;
        media.appendChild(img);
      }
      link.appendChild(media);

      // Brand sits above the title, matching the product cards elsewhere on the site.
      if (product.vendor) {
        const brand = document.createElement('span');
        brand.className = 'wishlist-card__brand';
        brand.textContent = product.vendor;
        link.appendChild(brand);
      }

      const title = document.createElement('span');
      title.className = 'wishlist-card__title';
      title.textContent = product.title || product.handle;
      link.appendChild(title);

      const price = document.createElement('span');
      price.className = 'wishlist-card__price';
      const onSale = product.compare_at_price && product.compare_at_price > product.price;
      const current = document.createElement('span');
      current.className = onSale ? 'wishlist-card__price-sale' : '';
      current.textContent = formatMoney(product.price);
      price.appendChild(current);
      if (onSale) {
        const was = document.createElement('s');
        was.className = 'wishlist-card__price-compare';
        was.textContent = formatMoney(product.compare_at_price);
        price.append(' ', was);
      }
      link.appendChild(price);

      if (product.available === false) {
        const badge = document.createElement('span');
        badge.className = 'wishlist-card__badge';
        badge.textContent = 'Sold out';
        link.appendChild(badge);
      }

      card.appendChild(link);
      card.appendChild(this.#buildRemoveButton(product.handle, product.title || product.handle));
      return card;
    }

    #buildErrorCard(handle) {
      const card = document.createElement('li');
      card.className = 'wishlist-card wishlist-card--error';
      card.dataset.handle = handle;

      const message = document.createElement('p');
      message.className = 'wishlist-card__message';
      message.textContent = "We couldn't load this product right now.";

      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'wishlist-card__retry';
      retry.textContent = 'Retry';
      retry.addEventListener('click', () => {
        this.#renderedKey = null;
        this.render();
      });

      card.append(message, retry, this.#buildRemoveButton(handle, handle));
      return card;
    }

    #buildRemoveButton(handle, title) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wishlist-card__remove';
      button.dataset.wishlistRemove = '';
      button.dataset.productTitle = title;
      button.setAttribute('aria-label', `Remove ${title} from wishlist`);
      button.textContent = 'Remove';
      return button;
    }
  }

  /* ------------------------------------------------------------------ define */

  if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
  if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
})();